import {
	Button,
	Collapse,
	createStyles,
	Hidden,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Theme,
	Typography,
	WithStyles,
	withStyles,
} from '@material-ui/core'
import { blueGrey, grey } from '@material-ui/core/colors'
import EditIcon from '@material-ui/icons/Edit'
import moment from 'moment'
import * as R from 'ramda'
import React from 'react'
import { NavLink } from 'react-router-dom'

import { ActivityParticipant } from '../../services/Model/Participant'
import { Sort, sortArrayItems } from '../../services/sortingService'
import { capitalize } from '../../services/utils'
import { TableItem } from '../TableWrapper'
import CustomTableHeader from './TableHeader/TableHeader'

export interface TableItemWithParticipantInfo extends TableItem, ActivityParticipant {

}

interface ActivityParticipantsTableProps extends WithStyles<typeof styles> {
	participants: TableItemWithParticipantInfo[]
	loading: boolean
	onParticipantInfoUpdate: () => void
}

interface ActivityParticipantsTableState {
	order: Sort
	orderBy: string
	page: number
	sortedParticipants: TableItemWithParticipantInfo[]
}

const PARTICIPANTS_TABLE_HEADERS = [
	{ label: 'Name', prop: 'lastName' },
	{ label: 'Enroll Date', prop: 'enrollmentDate' },
  // Removed: Outcome Actual Start Date (outcome.start_date) — no longer collected
  // { label: 'Participant Start Date', prop:'outcomeActualStartDate'},
	{ label: 'Status', prop: 'status' },
  { label: 'Quest', prop: 'quest'},
	{ label: 'Add/Edit/View Outcomes', prop: 'outcome' }
]

const setInitialState = (props: ActivityParticipantsTableProps): ActivityParticipantsTableState => {
	return {
		order: 'asc' as Sort,
		orderBy: 'lastName',
		page: 0,
		sortedParticipants: sortArrayItems(props.participants, 'asc', 'lastName', 0),
	}
}

export class ActivityParticipantsTable extends React.Component<
	ActivityParticipantsTableProps,
	ActivityParticipantsTableState
> {
	public readonly state: ActivityParticipantsTableState = setInitialState(this.props)

	handleRequestSort = (currentOrder: Sort, sortLabel: string) => () => {
		const { sortedParticipants, page } = this.state

		const order: Sort = currentOrder === 'asc' ? 'desc' : 'asc'
		const orderBy: string = sortLabel
		const participants = sortArrayItems(sortedParticipants, order, orderBy, page)

		this.updateSorting(participants, order, orderBy)
	}

	updateSorting = (participants: TableItemWithParticipantInfo[], order: Sort, sortLabel: string) => {
		this.setState({
			order,
			orderBy: sortLabel,
			sortedParticipants: participants,
		})
	}

	componentWillReceiveProps(nextProps: ActivityParticipantsTableProps) {
		if (R.equals(this.props.participants, nextProps.participants)) return

		const { order, orderBy, page } = this.state
		this.setState({ sortedParticipants: sortArrayItems(nextProps.participants, order, orderBy, page) })
	}

	checkContentItemAndFormat(contentItem: any) {
		if (contentItem.isDate) {
			if (contentItem !== undefined && contentItem.value !== "") {
        console.debug(`contentItem.value=${moment(contentItem.value).format('MM/DD/YYYY HH:mm')}`)
        return moment(contentItem.value).format('MM/DD/YYYY');
			}
			return '';
		}
		return contentItem.value ? capitalize(contentItem.value.replace('_', ' ')) : '';
	}

	render() {
		const { classes, loading } = this.props
		const { order, orderBy, sortedParticipants } = this.state

		return (
			<div className={classes.root} data-auto="participants-table">
				<Paper className={classes.root} square={true} elevation={2} data-auto="participants-table">
					<Collapse in={!loading} timeout={{ enter: 700 }}>
						{!!sortedParticipants.length ? (
							<Table component="div" className={classes.table} data-auto="main-table">
								<CustomTableHeader
									headers={PARTICIPANTS_TABLE_HEADERS}
									sortBy={orderBy}
									sortDirection={order}
									handleSortClick={this.handleRequestSort}
								/>
								<TableBody component="div" data-auto="table-body">
									{sortedParticipants.map((item: TableItemWithParticipantInfo, index: number) => {
										const { url, label } = item.title

										return (
											<TableRow component="div" hover={true} className={classes.row} key={index}>
												<TableCell component="div" variant="body" className={classes.mainColumn}>
													<NavLink
														to={`${url}${item.participantId}`}
														className={classes.linkBtn}
														data-auto="row-item-title-link"
													>
														<Typography
															variant="button"
															className={classes.link}
															data-auto="row-item-title-label"
														>
															{label}
														</Typography>
													</NavLink>
												</TableCell>
												{item.content.map((contentItem: any, i: number) => {
													return (
														<TableCell component="div" variant="body" key={i}  className={classes.tableCell}>
															<Typography
																variant="body1"
																color="textPrimary"
																style={{ display: 'inline-block' }}
                                // Removed: outcomeActualStartDate red highlight — no longer collected
                                // className={(item.enrollmentDate && item.outcomeActualStartDate && moment(item.outcomeActualStartDate).startOf('day').isBefore(moment(item.enrollmentDate).startOf('day')) && contentItem.label === 'Outcome Start Date')? classes.red : ''}
															>
																{this.checkContentItemAndFormat(contentItem)}
															</Typography>
														</TableCell>
													)
												})}

												<TableCell component="div" variant="body" align="right">
													<NavLink
														to={{
															pathname: `${url}${item.participantId}/outcomes`,
															state: { participantName: `${item.firstName} ${item.lastName}` },
														}}
														className={classes.linkBtn}
													>
														<Hidden smDown={true}>
															<Button variant="contained" className={classes.outcomes}>
																<EditIcon className={classes.icon} />
																Edit/Add Outcomes
															</Button>
														</Hidden>
													</NavLink>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						) : (
							<Typography variant={'h6'} component={'h3'} className={classes.emptyTable} color="secondary">
								No entries found.
							</Typography>
						)}
					</Collapse>
				</Paper>
			</div>
		)
	}
}

const styles = (theme: Theme) =>
	createStyles({
		root: {
			width: '100%',
			overflowX: 'auto',
			padding: '2px',
		},
		table: {},
		row: {
			'&:nth-of-type(odd)': {
				backgroundColor: grey[100],
			},
			'&:hover': {
				backgroundColor: blueGrey[300],
			},
		},
		linkBtn: {
			textDecoration: 'none',
		},
		link: {
			transition: 'transform 200ms, color 200ms, letter-spacing 500ms',
			textTransform: 'none',
			fontWeight: 500,
			'&:hover': {
				color: theme.palette.primary[900],
				[theme.breakpoints.up('md')]: {
					transform: 'scale(1, 1.25)',
					letterSpacing: '0.1em',
				},
			},
		},
		editStatus: {
			transition: 'transform 200ms, color 200ms',
			'&:hover': {
				color: theme.palette.primary[900],
			},
		},
		icon: {
			paddingRight: '8px'
		},
		emptyTable: {
			padding: `${theme.spacing.unit * 2}px`,
			textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
			fontWeight: 500,
			[theme.breakpoints.down('sm')]: {
				padding: `${theme.spacing.unit}px 0`,
			},
		},
		outcomes: {
			whiteSpace: 'nowrap'
		},
    red:{
      color: 'red'
    },
    mainColumn:{
      paddingRight:'0px',
      minWidth:'16em'
    },
    tableCell:{
      paddingRight:'0px'
    }
	})

export default withStyles(styles)(ActivityParticipantsTable)
